import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const STEPS = [
  { n: "01", t: "Apply", d: "Create a vendor account, submit your store details, and share your best three product photos." },
  { n: "02", t: "Inspection Fit", d: "We audit a sample batch. If your quality clears The Spotters, you're in." },
  { n: "03", t: "List & Ship-In", d: "List products. When a buyer orders, ship to the Hub. We handle the rest." },
  { n: "04", t: "Get Paid", d: "Payout hits your wallet within 5 days of successful inspection. GST invoicing handled." },
];

const PERKS = [
  "Zero listing fees",
  "Flat 12% commission",
  "Weekly payout cycle",
  "GST invoicing built-in",
  "Free QC photography",
  "Failure feedback loop",
  "Vendor-owned storefront page",
  "Curated placements on the home feed",
];

export default function Sell() {
  return (
    <div>
      <section className="bg-foreground text-background border-b border-foreground">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-widest text-background/60">For Makers · Small Houses · Independents</p>
            <h1 className="font-serif text-6xl md:text-9xl mt-4 leading-[0.85] tracking-tighter">
              Sell where every buyer already trusts the tag.
            </h1>
            <p className="mt-8 max-w-lg text-background/70 text-lg">
              We do the hard part — inspection, packaging, returns. You make the thing. Split: 88% to you, 12% to us. No listing fees.
            </p>
            <div className="mt-10 flex gap-4">
              <Link to="/become-a-vendor" data-testid="sell-cta-register" className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-background hover:text-foreground">
                Become a Vendor
              </Link>
              <Link to="/spotters" className="border border-background/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-background hover:text-foreground">
                Meet the Spotters
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="border-2 border-background p-8 tag-punch pl-14" style={{ background: "hsl(var(--foreground))" }}>
              <p className="font-mono text-[10px] uppercase tracking-widest text-background/60">Vendor Split · Standard Terms</p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <p className="font-serif text-7xl text-[hsl(var(--accent))]">88%</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-background/60 mt-2">Yours</p>
                </div>
                <div>
                  <p className="font-serif text-7xl text-background/40">12%</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-background/60 mt-2">The Hub</p>
                </div>
              </div>
              <div className="tick-row mt-8 opacity-30" style={{ backgroundImage: "repeating-linear-gradient(90deg,hsl(var(--background)) 0,hsl(var(--background)) 1px,transparent 1px,transparent 8px)" }} />
              <p className="mt-6 font-mono text-[11px] leading-relaxed">
                No hidden fees. GST filed on your behalf. Failure refunds deducted from vendor payout only if failure rate exceeds 5%.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Path In</p>
        <h2 className="font-serif text-5xl md:text-7xl mt-2 tracking-tighter mb-16">Four steps. No games.</h2>
        <div className="grid md:grid-cols-4 gap-10">
          {STEPS.map((s, i) => (
            <div key={s.n} className="border-t-2 border-foreground pt-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">{s.n}</p>
              <h3 className="font-serif text-3xl mt-2 tracking-tighter">{s.t}</h3>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-foreground bg-secondary">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 grid md:grid-cols-2 gap-16">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">What You Get</p>
            <h2 className="font-serif text-5xl md:text-6xl mt-2 tracking-tighter">Everything worth having.</h2>
            <p className="mt-6 text-muted-foreground">
              We built the tools we wanted as makers ourselves. No dashboards that punish you for being small.
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-4">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-start gap-3 border-b border-foreground/20 pb-3">
                <Check size={16} strokeWidth={2.5} className="mt-1 text-[hsl(var(--accent))]" />
                <span className="text-sm">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24">
        <div className="border-2 border-foreground p-12 md:p-16 tag-punch pl-16 md:pl-20">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ready?</p>
          <h2 className="font-serif text-5xl md:text-8xl mt-4 tracking-tighter leading-[0.9]">Apply in ten minutes.</h2>
          <p className="mt-6 max-w-lg text-muted-foreground">
            We review applications every Tuesday & Friday. Expect a reply within 3 working days.
          </p>
          <Link to="/become-a-vendor" className="inline-block mt-10 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-10 py-5 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground hover:text-background">
            Become a Vendor →
          </Link>
        </div>
      </section>
    </div>
  );
}
